import useScrollAnimation from '../../../hooks/useScrollAnimation';
import { spaceFeatures } from '../../../mocks/restaurant';

interface ZigzagItemProps {
  feature: typeof spaceFeatures[0];
}

const ZigzagItem = ({ feature }: ZigzagItemProps) => {
  const { ref, animStyle } = useScrollAnimation();

  return (
    <div
      ref={ref}
      style={animStyle}
      className={`flex flex-col ${feature.reverse ? 'md:flex-row-reverse' : 'md:flex-row'} gap-8 md:gap-14 items-center`}
    >
      {/* Image */}
      <div className="w-full md:w-1/2 flex-shrink-0">
        <div className="w-full h-64 md:h-80 overflow-hidden" style={{ borderRadius: '4px' }}>
          <img
            src={feature.image}
            alt={feature.title}
            className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-700"
          />
        </div>
      </div>

      {/* Text */}
      <div className="w-full md:w-1/2">
        <p className="font-sans-jp text-xs tracking-[0.3em] text-teal-deep mb-3 uppercase">
          {feature.title}
        </p>
        <h3 className="font-serif-jp font-bold text-brown-dark text-xl md:text-2xl leading-relaxed mb-5 whitespace-pre-line">
          {feature.heading}
        </h3>
        <div className="w-8 h-0.5 bg-orange-sunset mb-5" />
        <p className="font-sans-jp text-sm text-brown-mid leading-loose">
          {feature.body}
        </p>
      </div>
    </div>
  );
};

const SpaceSection = () => {
  const { ref: titleRef, animStyle: titleAnim } = useScrollAnimation();

  return (
    <section id="space" className="bg-cream-warm py-20 md:py-28 px-5 md:px-10">
      <div className="max-w-5xl mx-auto">
        {/* Section Title */}
        <div ref={titleRef} style={titleAnim} className="text-center mb-16">
          <p className="font-sans-jp text-xs tracking-[0.3em] text-teal-deep mb-3 uppercase">
            Space &amp; Service
          </p>
          <h2 className="font-serif-jp font-bold text-brown-dark text-2xl md:text-3xl tracking-wide mb-4">
            空間・サービス
          </h2>
          <div className="flex items-center justify-center gap-3 mt-4">
            <div className="h-px w-10 bg-teal-deep/30" />
            <div className="w-1.5 h-1.5 rounded-full bg-orange-sunset" />
            <div className="h-px w-10 bg-teal-deep/30" />
          </div>
        </div>

        {/* Zigzag Features */}
        <div className="flex flex-col gap-20">
          {spaceFeatures.map((feature) => (
            <ZigzagItem key={feature.id} feature={feature} />
          ))}
        </div>

        {/* Highlights Bar */}
        <SpaceHighlights />
      </div>
    </section>
  );
};

const SpaceHighlights = () => {
  const { ref, animStyle } = useScrollAnimation();

  const items = [
    { icon: 'ri-restaurant-line', label: 'カウンター席', sub: '8席（おひとり様歓迎）' },
    { icon: 'ri-group-line', label: 'テーブル席', sub: '24席（グループ対応可）' },
    { icon: 'ri-car-line', label: '駐車場', sub: '店舗隣接 6台' },
    { icon: 'ri-leaf-line', label: '禁煙', sub: '全席禁煙' },
  ];

  return (
    <div ref={ref} style={animStyle} className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-4">
      {items.map((item) => (
        <div
          key={item.label}
          className="bg-white rounded-lg p-5 text-center border border-brown-dark/8"
        >
          <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3">
            <i className={`${item.icon} text-2xl text-teal-deep`} />
          </div>
          <p className="font-serif-jp font-semibold text-brown-dark text-sm mb-1">{item.label}</p>
          <p className="font-sans-jp text-xs text-brown-mid">{item.sub}</p>
        </div>
      ))}
    </div>
  );
};

export default SpaceSection;
